import React, { useState, useEffect } from 'react';
import { Layout } from 'antd';
import { useDispatch, useSelector, batch } from 'react-redux';
import { isEmpty } from 'ramda';
import ConfiguredIntercom from '../components/ConfiguredIntercom';
import Header from '../components/shared/Header';
import Toolbar from '../components/Toolbar';
import CarTable from '../components/CarTable';
import AreaTable from '../components/AreaTable';
import ClaimTable from '../components/ClaimTable';
import CareTable from '../components/CareTable';
import TripsTable from '../components/TripsTable';
import BookingsTable from '../components/BookingsTable';
import Map from '../components/Mapbox';
import CarEditDrawer from '../components/CarEditDrawer';
import AreaEditDrawer from '../components/AreaEditDrawer';
import ClaimDrawer from '../components/ClaimDrawer';
import CareDrawer from '../components/CareDrawer';
import TripDrawer from '../components/TripDrawer';
import SideMenu from '../components/SideMenu';
import AppliedFilters from '../components/Filter/AppliedFilters';
import DamageModal from '../components/DamageModal';
import useViewMode from '../hooks/useViewMode';
import { ViewMode } from '../hooks/viewMode';
import useViewType, { ViewType } from '../hooks/useViewType';
import { setQueries, removeQueries } from '../libs/setQueries';
import { zIndex, colors } from '../theme/theme';
import AddServiceModal from '../components/AddServiceModal';
import { selectCares } from '../redux/store/userData/cares';
import { selectAreas } from '../redux/store/appData/areas';
import { selectSelectedCar, selectCars } from '../redux/store/userData/cars';
import {
  selectClaims,
  selectClaimsLoading,
} from '../redux/store/userData/claims';
import { selectTrips, selectBookings } from '../redux/store/userData/trips';
import PhoneReportModal from '../components/PhoneReportModal';
import IntercomReportModal from '../components/IntercomReportModal';
import {
  selectCarWithoutMapEffect,
  openModal,
  selectAreaWithUIEffect,
  selectViewMode,
  selectViewType,
  selectCategoryWithUIEffect,
  selectCircleWithUIEffect,
  selectPricingWithUIEffect,
  setViewMode,
  setViewType,
  selectProfileWithUIEffect,
  selectReceiptWithUIEffect,
  selectFuelTransactionWithUIEffect,
  selectPrivacyPolicyWithUIEffect,
  selectPurposeWithUIEffect,
  selectClaimWithUIEffect,
  selectCareWithUIEffect,
  selectTripWithUIEffect,
  selectBookingWithUIEffect,
} from '../redux/store/ui/common';
import { syncAndApplyFilter } from '../redux/store/ui/filters';
import {
  loginByLocalStorage,
  selectAuth,
  selectIsArchivedUsersManager,
} from '../redux/store/userData/user';
import { ARCHIVED_USER_STATUS } from '../components/Filter/assets/optionsForUsers';
import CategoriesTable from '../components/CategoriesTable';
import AddCategoryModal from '../components/AddCategoryModal';
import CategoryDrawer from '../components/CategoryEditDrawer/CategoryEditDrawer';
import { selectCategories } from '../redux/store/userData/categories';
import CircleTable from '../components/CircleTable';
import { selectCircles } from '../redux/store/userData/circles';
import CircleEditDrawer from '../components/CircleEditDrawer';
import AddCircleModal from '../components/AddCircleModal/AddCircleModal';
import CreateAreaModal from '../components/CreateAreaModal/CreateAreaModal';
import { selectPricings } from '../redux/store/appData/pricings';
import PricingsTable from '../components/PricingsTable';
import PricingEditDrawer from '../components/PricingEditDrawer';
import { useQuery } from '../hooks/useQuery';
import { history } from '../utils';
import BookingDrawer from '../components/BookingDrawer';
import BookingCreateModal from '../components/BookingCreateModal';
import { selectProfiles } from '../redux/store/userData/profiles';
import ProfilesTable from '../components/ProfilesTable';
import ProfileEditDrawer from '../components/ProfileEditDrawer';
import AddProfileModal from '../components/AddProfileModal/AddProfileModal';
import UpdateProfileModal from '../components/UpdateProfileModal/UpdateProfileModal';
import UsersTable from '../components/UsersTable';
import UsersDrawer from '../components/UsersDrawer';
import { BulkArchiveDrawer } from '../components/BulkActions';
import UserContactModal from '../components/UsersDrawer/UserContactModal/UserContactModal';
import { selectUsers } from '../redux/store/userData/users';
import {
  selectAdminWithUIEffect,
  selectUserWithUIEffect,
  selectReportWithUIEffect,
} from '../redux/store/ui/common/thunk';
import AddLicenseModal from '../components/UsersDrawer/LicenseModal/AddLicenseModal';
import EditLicenseModal from '../components/UsersDrawer/LicenseModal/EditLicenseModal';
import TerminateCarModal from '../components/CarEditDrawer/TerminateCarModal/TerminateCarModal';
import ArchiveAreaModal from '../components/AreaEditDrawer/ArchiveAreaModal/ArchiveAreaModal';
import OpeningHoursModal from '../components/AreaEditDrawer/OpeningHoursModal/OpeningHoursModal';
import AreaNoteModal from '../components/AreaEditDrawer/AreaNoteModal/AreaNoteModal';
import AdminsTable from '../components/AdminsTable';
import { selectAdmins } from '../redux/store/userData/admins';
import AdminsDrawer from '../components/AdminsDrawer';
import AddAdminModal from '../components/AdminsDrawer/AddAdminModal/AddAdminModal';
import AdminDeleteModal from '../components/AdminsDrawer/AdminDeleteModal/AdminDeleteModal';
import VehicleTimeline from '../components/VehicleTimeline';
import TripCancelModal from '../components/TripDrawer/TripCancelModal/TripCancelModal';
import BookingCancelModal from '../components/BookingDrawer/BookingCancelModal/BookingCancelModal';
import CareCancelModal from '../components/CareDrawer/CareCancelModal/CareCancelModal';
import BookingVehicleEditModal from '../components/BookingDrawer/BookingVehicleEditModal/BookingVehicleEditModal';
import TripReturnTimeEditModal from '../components/TripDrawer/TripReturnTimeEditModal/TripReturnTimeEditModal';
import BookingStartEndEditModal from '../components/BookingDrawer/BookingStartEndEditModal/BookingStartEndEditModal';
import EditAdminInfoModal from '../components/AdminsDrawer/EditAdminInfoModal/EditAdminInfoModal';
import BookingUserEditModal from '../components/BookingDrawer/BookingUserEditModal/BookingUserEditModal';
import ReceiptsTable from '../components/ReceiptsTable';
import FuelTransactionsTable from '../components/FuelTransactionsTable';
import ReceiptDrawer from '../components/ReceiptDrawer';
import FuelTransactionDrawer from '../components/FuelTransactionDrawer';
import { selectReceipts } from '../redux/store/userData/receipts/selectors';
import ReportsTable from '../components/ReportsTable';
import ReportDrawer from '../components/ReportDrawer';
import UserIdentityModal from '../components/UsersDrawer/UserIdentityModal/UserIdentityModal';
import ClaimDamageModal from '../components/ClaimDrawer/ClaimDamageModal/ClaimDamageModal';
import ClaimTimelineAddModal from '../components/ClaimDrawer/ClaimTimelineAddModal/ClaimTimelineAddModal';
import ClaimImageAddModal from '../components/ClaimDrawer/ClaimImageAddModal/ClaimImageAddModal';
import ClaimImageAnnotateModal from '../components/ClaimDrawer/ClaimImageAnnotateModal/ClaimImageAnnotateModal';
import ConfirmModal from '../components/shared/ConfirmModal/ConfirmModal';
import UserLicenseSettingsModal from '../components/UsersDrawer/UserLicenseSettingsModal/UserLicenseSettingsModal';
import UserPaymentModal from '../components/UsersDrawer/UserPaymentModal/UserPaymentModal';
import StatusModal from '../components/shared/StatusModal/StatusModal';
import UserIDSettingsModal from '../components/UsersDrawer/UserIDSettingsModal/UserIDSettingsModal';
import ReceiptsCreateModal from '../components/ReceiptDrawer/ReceiptsCreateModal/ReceiptsCreateModal';
import BookingInsuranceEditModal from '../components/BookingDrawer/BookingInsuranceEditModal/BookingInsuranceEditModal';
import BookingCircleEditModal from '../components/BookingDrawer/BookingCircleEditModal/BookingCircleEditModal';
import AddIDModal from '../components/UsersDrawer/IDModal/AddIDModal';
import EditIDModal from '../components/UsersDrawer/IDModal/EditIDModal';
import UserStatusModal from '../components/UsersDrawer/UserStatusModal/UserStatusModal';
import UsersCreateModal from '../components/UsersDrawer/UsersCreateModal/UsersCreateModal';
import BulkCreateUsersDrawer from '../components/UsersDrawer/UsersCreateModal/BulkCreateUsersDrawer';
import UserOrgModal from '../components/UsersDrawer/UserOrgModal/UserOrgModal';
import UserOrgContactModal from '../components/UsersDrawer/UserOrgContactModal/UserOrgContactModal';
import EbusOptionsModal from '../components/UsersDrawer/UserStatusTab/EbusOptionsModal/EbusOptionsModal';
import CreateButton from '../components/shared/CreateButton/CreateButton';
import { selectPrivacyPolicies } from '../redux/store/appData/privacyPolicy';
import PrivacyPoliciesTable from '../components/PrivacyPoliciesTable';
import PolicyDrawer from '../components/PrivacyPoliciesDrawer';
import PolicyStatusModal from '../components/PrivacyPoliciesDrawer/PolicyInfoTab/PolicyStatusModal/PolicyStatusModal';
import PurposeDrawer from '../components/PurposeDrawer';
import PurposeStatusModal from '../components/PurposeDrawer/PurposeInfoTab/PurposeStatusModal/PurposeStatusModal';
import UserBICModal from '../components/UsersDrawer/UserBICModal/UserBICModal';
import UserManualApprovalModal from '../components/UsersDrawer/UserManualApprovalModal/UserManualApprovalModal';
import UserTariffSettingsModal from '../components/UsersDrawer/UserTariffSettingsModal/UserTariffSettingsModal';
import ConfirmBlockModal from '../components/CarEditDrawer/ConfirmBlockModal';
import UpdateAreaNameModal from '../components/AreaEditDrawer/UpdateAreaNameModal';
import UpdateAreaDescriptionModal from '../components/AreaEditDrawer/UpdateAreaDescriptionModal';
import LicensesModal from '../components/LicensesModal';
import UpdateAreaFleetStyleModal from '../components/AreaEditDrawer/UpdateAreaFleetStyleModal';

const Dashboard = () => {
  const dispatch = useDispatch();
  const [damage, setDamage] = useState(null);
  const [isDeepLinkHandled, setIsDeepLinkHandled] = useState(false);
  const [isDamageIdAutoOpened, setIsDamageIdAutoOpened] = useState(false);
  const [isInitialSyncDone, setIsInitialSyncDone] = useState(false);
  const { initViewMode } = useViewMode();
  const { initViewType } = useViewType();
  const viewMode = useSelector(selectViewMode);
  const viewType = useSelector(selectViewType);
  const user = useSelector(selectAuth);
  const isArchivedUsersManager = useSelector(selectIsArchivedUsersManager);
  const carsEntities = useSelector(selectCars);
  const selectedCar = useSelector(selectSelectedCar);
  const areasEntities = useSelector(selectAreas);
  const careEntities = useSelector(selectCares);
  const tripEntities = useSelector(selectTrips);
  const bookingEntities = useSelector(selectBookings);
  const claimEntities = useSelector(selectClaims);
  const claimsLoading = useSelector(selectClaimsLoading);
  const circleEntities = useSelector(selectCircles);
  const categoryEntities = useSelector(selectCategories);
  const pricingEntities = useSelector(selectPricings);
  const privacyPolicyEntities = useSelector(selectPrivacyPolicies);
  const profileEntities = useSelector(selectProfiles);
  const usersEntities = useSelector(selectUsers);
  const adminsEntities = useSelector(selectAdmins);
  const receiptsEntities = useSelector(selectReceipts);

  const {
    vm,
    type,
    carId,
    carTab,
    areaId,
    claimId,
    careId,
    tripId,
    bookingId,
    damageId,
    categoryId,
    pricingId,
    policyId,
    purposeId,
    profileId,
    circleId,
    userId,
    adminId,
    receiptId,
    fuelTransactionId,
    reportId,
    pageNumber,
    pageSize,
    ...filters
  } = useQuery();

  const openDamageModalFromUrl = () => {
    if (vm === ViewMode.VEHICLE && damageId != null && selectedCar != null) {
      const _damage = selectedCar.damages.find(
        (d) => String(d.id) === damageId
      );

      if (_damage != null) {
        setDamage(_damage);
        dispatch(openModal('damageModal'));
      }
    }
  };

  const openDrawerByQuery = () => {
    if (vm === ViewMode.VEHICLE && carId != null && !isDeepLinkHandled) {
      setIsDeepLinkHandled(true);
      dispatch(
        selectCarWithoutMapEffect(
          { id: carId },
          true,
          carTab ? parseInt(carTab, 10) : 0
        )
      );
    }

    if (vm === ViewMode.PROFILE && profileId != null && !isDeepLinkHandled) {
      setIsDeepLinkHandled(true);
      dispatch(selectProfileWithUIEffect({ id: profileId }));
    }

    if (vm === ViewMode.USERS && userId != null && !isDeepLinkHandled) {
      setIsDeepLinkHandled(true);
      dispatch(selectUserWithUIEffect({ id: userId }));
    }

    if (vm === ViewMode.ADMINS && adminId != null && !isDeepLinkHandled) {
      setIsDeepLinkHandled(true);
      dispatch(selectAdminWithUIEffect({ id: adminId }));
    }

    if (vm === ViewMode.AREA && areaId != null && !isDeepLinkHandled) {
      setIsDeepLinkHandled(true);
      dispatch(selectAreaWithUIEffect({ id: areaId }));
    }

    if (
      vm === ViewMode.CATEGORIES &&
      categoryId != null &&
      !isDeepLinkHandled
    ) {
      setIsDeepLinkHandled(true);
      dispatch(selectCategoryWithUIEffect({ id: categoryId }));
    }

    if (vm === ViewMode.PRICING && pricingId != null && !isDeepLinkHandled) {
      setIsDeepLinkHandled(true);

      dispatch(selectPricingWithUIEffect({ id: pricingId }));
    }

    if (
      vm === ViewMode.PRIVACY_POLICY &&
      policyId != null &&
      !isDeepLinkHandled
    ) {
      setIsDeepLinkHandled(true);
      dispatch(selectPrivacyPolicyWithUIEffect({ id: policyId }));
    }

    if (
      vm === ViewMode.PRIVACY_POLICY &&
      purposeId != null &&
      !isDeepLinkHandled
    ) {
      setIsDeepLinkHandled(true);
      dispatch(selectPurposeWithUIEffect({ id: purposeId }));
    }

    if (vm === ViewMode.RECEIPTS && receiptId != null && !isDeepLinkHandled) {
      setIsDeepLinkHandled(true);
      dispatch(selectReceiptWithUIEffect({ id: receiptId }));
    }

    if (
      vm === ViewMode.FUEL_TRANSACTIONS &&
      fuelTransactionId != null &&
      !isDeepLinkHandled
    ) {
      setIsDeepLinkHandled(true);
      dispatch(selectFuelTransactionWithUIEffect({ id: fuelTransactionId }));
    }

    if (vm === ViewMode.REPORTS && reportId != null && !isDeepLinkHandled) {
      setIsDeepLinkHandled(true);
      dispatch(selectReportWithUIEffect({ id: reportId }));
    }

    if (
      vm === ViewMode.CIRCLE &&
      circleId != null &&
      !isDeepLinkHandled &&
      user.permissions?.userGroupsManager
    ) {
      setIsDeepLinkHandled(true);
      dispatch(selectCircleWithUIEffect({ id: circleId }));
    }

    if (
      vm === ViewMode.CLAIM &&
      claimId != null &&
      !isDeepLinkHandled &&
      user.permissions?.claimsManager
    ) {
      setIsDeepLinkHandled(true);
      dispatch(selectClaimWithUIEffect({ id: claimId }));
    }

    if (vm === ViewMode.CARE && careId != null && !isDeepLinkHandled) {
      setIsDeepLinkHandled(true);
      dispatch(selectCareWithUIEffect({ id: careId }));
    }

    if (vm === ViewMode.SHARE && tripId != null && !isDeepLinkHandled) {
      setIsDeepLinkHandled(true);
      dispatch(selectTripWithUIEffect({ id: tripId }));
    }

    if (vm === ViewMode.BOOKING && bookingId != null && !isDeepLinkHandled) {
      setIsDeepLinkHandled(true);
      dispatch(selectBookingWithUIEffect({ id: bookingId }));
    }
  };

  // initialize
  useEffect(() => {
    dispatch(loginByLocalStorage());
    const initialViewMode = initViewMode();
    const initialViewType = initViewType();
    const queries = { ...initialViewMode, ...initialViewType };

    if (initialViewMode != null && vm != null) {
      setQueries({ type: ViewType.TABLE, ...initialViewMode }, true);
    } else if (initialViewMode != null || initialViewType != null) {
      setQueries(queries);
    }

    const url = new URLSearchParams(history.location.search);
    dispatch(setViewMode(url.get('vm')));
    dispatch(setViewType(url.get('type')));
    // gtag.pageview();
  }, []);

  // after loading all entities - only run once when data is available
  useEffect(() => {
    if (isInitialSyncDone) return;

    // Check if at least some entities have loaded
    const hasLoadedEntities =
      carsEntities?.length > 0 ||
      areasEntities?.length > 0 ||
      tripEntities?.length > 0 ||
      usersEntities?.length > 0;

    const isUsersViewReady = viewMode === ViewMode.USERS && user?.id != null;

    if (!hasLoadedEntities && !isUsersViewReady) return;

    setIsInitialSyncDone(true);

    // set filter by url queries
    const appliedFilters = { ...filters };
    if (
      viewMode === ViewMode.USERS &&
      appliedFilters.accountStatus != null &&
      !isArchivedUsersManager
    ) {
      const allowedStatuses = String(appliedFilters.accountStatus)
        .split(',')
        .filter((status) => status && status !== ARCHIVED_USER_STATUS);
      if (allowedStatuses.length > 0) {
        appliedFilters.accountStatus = allowedStatuses;
        setQueries({ accountStatus: allowedStatuses.join(',') });
      } else {
        delete appliedFilters.accountStatus;
        removeQueries({ accountStatus: '' });
      }
    }
    if (viewMode === ViewMode.USERS && appliedFilters.accountStatus == null) {
      appliedFilters.accountStatus = ['active'];
    }
    if (viewMode === ViewMode.CLAIM && damageId != null) {
      appliedFilters.damageId = damageId;
    }
    if (!isEmpty(appliedFilters)) {
      dispatch(
        syncAndApplyFilter({
          viewMode,
          value: appliedFilters,
        })
      );
    }
    openDamageModalFromUrl();
    openDrawerByQuery();
  }, [
    isInitialSyncDone,
    carsEntities?.length,
    areasEntities?.length,
    careEntities?.length,
    tripEntities?.length,
    bookingEntities?.length,
    claimEntities?.length,
    circleEntities?.length,
    pricingEntities?.length,
    profileEntities?.length,
    usersEntities?.length,
    receiptsEntities?.length,
    viewMode,
    user?.id,
  ]);

  useEffect(() => {
    if (
      vm === ViewMode.CLAIM &&
      damageId != null &&
      !isDamageIdAutoOpened &&
      user.permissions?.claimsManager &&
      !claimsLoading &&
      claimEntities?.length === 1
    ) {
      setIsDamageIdAutoOpened(true);
      dispatch(selectClaimWithUIEffect({ id: claimEntities[0].id }));
    }
  }, [
    vm,
    damageId,
    claimEntities,
    claimsLoading,
    user.permissions?.claimsManager,
  ]);

  useEffect(() => {
    return history.listen(() => {
      const url = new URLSearchParams(history.location.search);
      dispatch(setViewMode(url.get('vm')));
      dispatch(setViewType(url.get('type')));
    });
  }, [history]);

  const openNav = () => {
    document.getElementById('sidebar').style.width = '220px';
    document.getElementById('main').style.marginLeft = '220px';
    document.getElementById('main-header').style.left = '220px';
  };

  return (
    <Layout>
      <button
        data-testid="Dashboard-sidebar-openbtn"
        className="openbtn"
        onClick={openNav}
      >
        ☰
      </button>
      <SideMenu />
      <div id="main">
        <Layout>
          <div className="wrapper">
            <div className="main-wrapper">
              <div id="main-header" className="main-header">
                <Header />
                <Toolbar />
              </div>
              <div className="main-body">
                <AppliedFilters viewMode={viewMode} viewType={viewType} />
                {viewMode === ViewMode.SHARE ? (
                  <TripsTable />
                ) : viewMode === ViewMode.BOOKING ? (
                  <BookingsTable />
                ) : viewMode === ViewMode.CARE ? (
                  <CareTable />
                ) : viewType === ViewType.MAP ? (
                  <Map />
                ) : viewType === ViewType.TIME_TABLE ? (
                  <VehicleTimeline />
                ) : viewMode === ViewMode.AREA ? (
                  <AreaTable />
                ) : viewMode === ViewMode.CLAIM ? (
                  <ClaimTable />
                ) : viewMode === ViewMode.CATEGORIES ? (
                  <CategoriesTable />
                ) : viewMode === ViewMode.PRICING ? (
                  <PricingsTable />
                ) : viewMode === ViewMode.PRIVACY_POLICY ? (
                  <PrivacyPoliciesTable />
                ) : viewMode === ViewMode.RECEIPTS ? (
                  <ReceiptsTable />
                ) : viewMode === ViewMode.FUEL_TRANSACTIONS ? (
                  <FuelTransactionsTable />
                ) : viewMode === ViewMode.REPORTS ? (
                  <ReportsTable />
                ) : viewMode === ViewMode.PROFILE ? (
                  <ProfilesTable />
                ) : viewMode === ViewMode.CIRCLE ? (
                  <CircleTable />
                ) : viewMode === ViewMode.USERS ? (
                  <UsersTable />
                ) : viewMode === ViewMode.ADMINS ? (
                  <AdminsTable />
                ) : viewMode === ViewMode.VEHICLE ? (
                  <CarTable />
                ) : null}
              </div>
            </div>
          </div>
          {viewMode === ViewMode.VEHICLE && <CarEditDrawer />}
          {viewMode === ViewMode.PROFILE && <ProfileEditDrawer />}
          {(viewMode === ViewMode.AREA ||
            (viewMode === ViewMode.VEHICLE &&
              viewType === ViewType.TIME_TABLE)) && <AreaEditDrawer />}
          {viewMode === ViewMode.CLAIM && <ClaimDrawer />}
          {viewMode === ViewMode.CARE && <CareDrawer />}
          {(viewMode === ViewMode.SHARE ||
            (viewMode === ViewMode.VEHICLE &&
              viewType === ViewType.TIME_TABLE)) && <TripDrawer />}
          {(viewMode === ViewMode.BOOKING ||
            (viewMode === ViewMode.VEHICLE &&
              viewType === ViewType.TIME_TABLE)) && <BookingDrawer />}
          {viewMode === ViewMode.CATEGORIES && <CategoryDrawer />}
          {viewMode === ViewMode.PRICING && <PricingEditDrawer />}
          {viewMode === ViewMode.CIRCLE && <CircleEditDrawer />}
          {viewMode === ViewMode.USERS && <UsersDrawer />}
          {viewMode === ViewMode.USERS && <BulkCreateUsersDrawer />}
          {viewMode === ViewMode.USERS && <BulkArchiveDrawer />}
          {viewMode === ViewMode.PRIVACY_POLICY && <PolicyDrawer />}
          {viewMode === ViewMode.PRIVACY_POLICY && <PurposeDrawer />}
          {viewMode === ViewMode.ADMINS && <AdminsDrawer />}
          {viewMode === ViewMode.RECEIPTS && <ReceiptDrawer />}
          {viewMode === ViewMode.FUEL_TRANSACTIONS && <FuelTransactionDrawer />}
          {viewMode === ViewMode.REPORTS && <ReportDrawer />}
          <DamageModal
            damage={damage}
            carPlate={selectedCar?.plate}
            onClose={() => {
              removeQueries({ damageId });
            }}
          />
          <AddServiceModal />
          <PhoneReportModal />
          <IntercomReportModal />
          <AddCategoryModal />
          <AddCircleModal />
          <AddProfileModal />
          <UpdateProfileModal />
          <CreateAreaModal />
          <UserContactModal />
          <UserOrgContactModal />
          <BookingCreateModal />
          <AddLicenseModal />
          <EditLicenseModal />
          <AddIDModal />
          <EditIDModal />
          <TerminateCarModal />
          <ArchiveAreaModal />
          <OpeningHoursModal />
          <AreaNoteModal />
          <UpdateAreaNameModal />
          <UpdateAreaDescriptionModal />
          <AddAdminModal />
          <AdminDeleteModal />
          <EditAdminInfoModal />
          <TripCancelModal />
          <BookingCancelModal />
          <CareCancelModal />
          <BookingVehicleEditModal />
          <BookingStartEndEditModal />
          <TripReturnTimeEditModal />
          <BookingUserEditModal />
          <BookingInsuranceEditModal />
          <BookingCircleEditModal />
          <UserIdentityModal />
          <ClaimDamageModal />
          <ClaimTimelineAddModal />
          <ClaimImageAddModal />
          <ClaimImageAnnotateModal />
          <UserOrgModal />
          <UserLicenseSettingsModal />
          <UserIDSettingsModal />
          <UserStatusModal />
          <UserPaymentModal />
          <ConfirmModal />
          <StatusModal />
          <ReceiptsCreateModal />
          <UsersCreateModal />
          <EbusOptionsModal />
          <PolicyStatusModal />
          <PurposeStatusModal />
          <UserBICModal />
          <UserManualApprovalModal />
          <UserTariffSettingsModal />
          <UpdateAreaFleetStyleModal />
          <LicensesModal />

          <ConfiguredIntercom />
          <CreateButton />
        </Layout>
      </div>
      <style jsx>
        {`
          #main {
            transition: margin-left 0.5s;
            padding: 0px;
          }
          .wrapper {
            display: flex;
          }
          .openbtn {
            font-size: 18px;
            cursor: pointer;
            background-color: #dff259;
            color: black;
            padding: 5px 10px;
            margin: 10px;
            border: none;
            border-radius: 50%;
            position: fixed;
            top: 0;
            left: 0;
            z-index: 1000;
            box-shadow: rgb(0 0 0 / 10%) 0 1px 4px 0,
              rgb(0 0 0 / 19%) 0 2px 6px 0;
          }
          .list-wrapper {
            width: 400px;
            border-right: 2px solid #2b2e42;
          }
          .main-wrapper {
            width: 100%;
            height: 100%;
            position: relative;
          }
          .main-header {
            z-index: ${zIndex.header};
            position: fixed;
            width: 100%;
            top: 0;
          }
          .main-body {
            width: 100%;
            height: 100vh;
            padding-top: ${viewMode !== ViewMode.CARE && type === ViewType.MAP
              ? 64
              : 116}px;
            background: ${colors.background};
            overflow: hidden;
          }
          :global(.grecaptcha-badge) {
            visibility: hidden;
          }
          @media only screen and (min-width: 768px) {
            #main {
              margin-left: 200px !important;
              padding: 0px;
            }
            .openbtn {
              display: none;
            }
            .main-wrapper {
              width: calc(100vw - 200px);
              height: 100%;
              position: relative;
            }
            .main-header {
              z-index: ${zIndex.header};
              position: fixed;
              width: calc(100vw - 200px);
              top: 0;
            }
          }
        `}
      </style>
    </Layout>
  );
};

export default Dashboard;
